/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { keysToCamel, keysToSnake } from '@/utils';

import { Audit } from './types';
import { StorageFolder, ListFolders } from './components/storage_integration/types';
import { getRequest, postRequest } from '../../utils/client/client';

const service = {
  async load(auditId: number) {
    const url = `/api/v2/auditsetup/${auditId}`;

    try {
      const data = await getRequest({ url });
      return keysToCamel(data.audit);
    } catch (error) {
      throw error;
    }
  },
  async loadInfo() {
    const url = '/api/v2/auditsetup/info';

    try {
      const data = await getRequest({ url });
      const info = keysToCamel(data);
      return {
        currentUser: info.audit.auditManagers,
        jira: info.jiraEnabled,
        servicenow: info.servicenowEnabled,
        flagNewTicketing: info.flagNewTicketing,
        templateAssessors: info.defaultAssessorsChoices,
        templateVerifiers: info.defaultVerifiersChoices,
      };
    } catch (error) {
      throw error;
    }
  },
  async save(audit: Audit, csrfToken: string) {
    // Either creating new or updating existing
    const target = audit.id === -1 ? 'new' : audit.id;
    const url = `/api/v2/auditsetup/${target}`;

    try {
      const data = await postRequest({
        url,
        data: keysToSnake({ audit }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });
      return keysToCamel(data.audit);
    } catch (error) {
      throw error;
    }
  },
  async start(audit: Audit, csrfToken: string) {
    // Either creating new or updating existing
    const target = audit.id === -1 ? 'new' : audit.id;
    const url = `/api/v2/auditsetup/${target}/start`;

    try {
      const data = await postRequest({
        url,
        data: null,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });
      return keysToCamel(data);
    } catch (error) {
      throw error;
    }
  },
  async validateTitle(title: string) {
    const url = '/api/v2/auditsetup/check_title';

    try {
      const data = await getRequest({
        url,
        params: { title },
      });
      return data['title_available'];
    } catch (error) {
      throw error;
    }
  },
  async validateStorageType(type: string) {
    const url = <any>{
      JIRA: '/jira/is_configured',
      SERVICENOW: '/servicenow/is_configured',
    };

    if (type === 'ZENGRC') {
      return true;
    }

    try {
      const data = await getRequest({ url: url[type] });
      return data;
    } catch (error) {
      throw error;
    }
  },

  // JIRA bus
  // FIXME - move to AuditInfoJira component
  async loadJiraProjects() {
    return getRequest({ url: '/jira/get_projects' });
  },
  async loadJiraProject(projectId: string) {
    return getRequest({ url: '/jira/get_project', params: { id: projectId } });
  },
  async loadJiraIssueLinkTypes() {
    return getRequest({ url: '/jira/get_issue_link_types' });
  },

  // Assessment Template
  async saveAssessmentTemplate(template: any, id: string, csrfToken: string) {
    const url = `/api/v2/auditsetup/${id}/assessment_template`;

    try {
      const data = await postRequest({
        url,
        data: { assessment_template: template },
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });
      return keysToCamel(data);
    } catch (error) {
      throw error;
    }
  },
  // Storage integration
  async getIsListingFoldersSupported(): Promise<boolean> {
    const url = '/storage/documents/list_folders_supported';

    const data = await getRequest({ url });
    const resData = keysToCamel(data);

    return resData.isSupported;
  },
  async getFolder(auditId: number): Promise<StorageFolder | null> {
    const url = `/storage/documents/get_folder/Audit/${auditId}`;

    const data = await getRequest({ url });
    const resData: StorageFolder | null = keysToCamel(data) ? data : null;

    return resData;
  },
  async getListFolders(storageFolder: StorageFolder): Promise<ListFolders> {
    let url = '/storage/documents/list_folders';

    const integrationId: number | null = storageFolder.integrationId;
    const location: string | null = storageFolder.location;

    if (integrationId && location) {
      url =
        `${url}?integration_id=${encodeURIComponent(String(integrationId))}` +
        `&location=${encodeURIComponent(location)}`;
    }

    const data = await getRequest({ url });
    const resData = keysToCamel(data);

    return resData;
  },
  async loadRemoteRequests(auditId: number, source: string) {
    const url = source === 'JIRA' ? '/jira/get_issues_for' : '/servicenow/get_tasks_for';

    const data = await getRequest({
      url,
      params: {
        audit_id: auditId,
      },
    });
    return data;
  },
};

export default service;

export interface Service {
  load(target: number): Promise<Audit>;
  loadInfo(): Promise<any>;
  save(audit: Audit, csrfToken: string): Promise<Audit>;
  start(audit: Audit, csrfToken: string): Promise<any>;
  validateTitle(title: string): boolean;
  validateStorageType(type: string): boolean;
  loadJiraProjects(): Promise<any>;
  loadJiraProject(projectId: number): Promise<any>;
  loadJiraIssueLinkTypes(): Promise<any>;
  loadRemoteRequests(auditId: number, source: string): Promise<any>;
  saveAssessmentTemplate(template: any, id: string, csrfToken: string): Promise<any>;
  // Storage integration
  getIsListingFoldersSupported(): Promise<boolean>;
  getFolder(target: number): Promise<any>;
  getListFolders(integrationId: number | null, location: string | null): Promise<ListFolders>;
}
